import React, { useMemo } from 'react';
import {
  formatChatTime,
  renderTextWithLinks,
  splitCombinedMessage,
  TIP_MESSAGE_REGEX,
  normalizeMessageString,
  parseTipJson,
  parseMediaJson,
  parseMissedCallNotice,
  parseEndedCallNotice,
} from '../../../../../shared/utils/chatUtil';
import ChatMediaMessage from './ChatMediaMessage';
import ChatMissedCall from './ChatMissedCall';
import ChatEndedCall from './ChatEndedCall';
import ChatMessageStatus from './ChatMessageStatus';

/**
 * ChatCard Component
 * Displays a single message bubble in the chat thread.
 * @param {Object} props
 * @param {Object} props.item - Message item
 * @param {boolean} props.item.isMine - True if the message is sent by current user
 * @param {string} [props.item.message] - Message text
 * @param {string} [props.item.time] - Pre-formatted time label
 * @param {number} [props.item.ts] - Timestamp (ms) used to format time label if `time` is missing
 * @param {string} props.otherAvatar - Avatar to show for incoming messages
 * @param {Object} [props.creatorInfo] - Creator info (e.g. username, profile_picture) for purchase modal in chat
 * @param {Array} [props.messagePurchaseContentIds] - Array of content IDs that have been purchased
 * @returns {JSX.Element}
 */
const ChatCard = ({
  item,
  otherAvatar,
  isUser,
  hasPriority = false,
  isCreatorReplyToTip = false,
  creatorInfo,
  messagePurchaseContentIds,
  onEditMessage = null,
  roomId = null,
  onCallBack = null,
}) => {
  const isMine = !!item?.isMine;
  const message = normalizeMessageString(item?.message);
  const pending = !!item?.pending;
  const failed = !!item?.failed;

  const missedCallInfo = useMemo(() => parseMissedCallNotice(message), [message]);
  const endedCallInfo = useMemo(() => parseEndedCallNotice(message), [message]);
  const parsedTipJson = useMemo(() => parseTipJson(message), [message]);
  const parsedMediaJson = useMemo(() => parseMediaJson(message), [message]);

  const hasMedia = useMemo(
    () =>
      !!(
        parsedMediaJson &&
        Array.isArray(parsedMediaJson.assets) &&
        parsedMediaJson.assets.length
      ),
    [parsedMediaJson]
  );

  const { mainText, tipLine, isTipRefunded, isTipPendingCollection } = useMemo(() => {
    // New JSON-based message tip: message body is a JSON string with metadata.
    if (parsedTipJson) {
      const main = typeof parsedTipJson.message === 'string' ? parsedTipJson.message : '';
      // Subscriber (sender) should see full charged amount; creator (receiver) should see realPrice.
      const rawAmount = isMine
        ? parsedTipJson.amount ?? parsedTipJson.baseAmount ?? parsedTipJson.realPrice
        : parsedTipJson.realPrice ?? parsedTipJson.amount ?? parsedTipJson.baseAmount;

      let tipAmountLabel = null;
      if (typeof rawAmount === 'number' || typeof rawAmount === 'string') {
        const num = Number(rawAmount);
        if (Number.isFinite(num)) {
          tipAmountLabel = num === Math.trunc(num) ? String(Math.trunc(num)) : num.toFixed(2);
        }
      }

      const refunded = !!parsedTipJson.isRefunded;
      const isFirstMessageTip = Boolean(parsedTipJson.isFirstMessageTip);
      // When it's the first tip and the conversation is not yet in Priority,
      // the tip is effectively "pending" (awaiting the other party's reply).
      // - Subscriber view should see pending on their own tip message.
      // - Creator view should see pending on the incoming subscriber tip message.
      const shouldShowPendingToCollect =
        isFirstMessageTip &&
        !hasPriority &&
        !refunded &&
        isMine === isUser;

      let tip = null;
      if (tipAmountLabel) {
        if (!isCreatorReplyToTip && shouldShowPendingToCollect) {
          tip = isUser
            ? `$${tipAmountLabel} pending • waiting for creator reply`
            : `$${tipAmountLabel} pending • reply to collect`;
        } else if (!refunded && isFirstMessageTip) {
          tip = `$${tipAmountLabel} tipped`;
        } else if (refunded && isFirstMessageTip) {
          tip = `$${tipAmountLabel} refunded`;
        } else {
          const sign = refunded ? '-' : '+';
          tip = `${sign}$${tipAmountLabel} tipped`;
        }
      }

      return {
        mainText: main,
        tipLine: tip,
        isTipRefunded: refunded,
        isTipPendingCollection: !isCreatorReplyToTip && shouldShowPendingToCollect,
      };
    }

    // Legacy text-based combined message: "text\n+$X tipped"
    const [main, tip] = splitCombinedMessage(message);
    return { mainText: main, tipLine: tip, isTipRefunded: false, isTipPendingCollection: false };
  }, [message, parsedTipJson, isMine, isUser, hasPriority, isCreatorReplyToTip]);

  const isTipOnlyMessage = useMemo(() => {
    if (parsedTipJson) {
      const hasMain = typeof parsedTipJson.message === 'string' && parsedTipJson.message.trim();
      return !hasMain;
    }
    return !tipLine && TIP_MESSAGE_REGEX.test(message.trim());
  }, [parsedTipJson, tipLine, message]);

  const { time, ts } = item || {};

  const timeLabel =
    typeof time === 'string'
      ? time
      : typeof ts === 'number'
        ? formatChatTime(ts)
        : '';

  // Unanswered-call notice is sent by the caller. Display: initiator sees "Outgoing Voice/Video Call";
  // recipient sees "Missed Voice/Video Call". Bubble side: right = you sent it; left = they sent.
  if (missedCallInfo) {
    return (
      <ChatMissedCall
        missedCallInfo={missedCallInfo}
        roomId={roomId}
        pending={pending}
        failed={failed}
        timeLabel={timeLabel}
        isMine={isMine}
        otherAvatar={otherAvatar}
        onCallBack={onCallBack}
      />
    );
  }

  if (endedCallInfo) {
    return (
      <ChatEndedCall
        endedCallInfo={endedCallInfo}
        pending={pending}
        failed={failed}
        timeLabel={timeLabel}
        isMine={isMine}
        otherAvatar={otherAvatar}
      />
    );
  }

  return (
    <div
      className={`cm_chat ${isMine ? 'your_msg' : 'incomming_msg'} ${isTipOnlyMessage ? 'cm_chat_tip' : ''}`}
    >
      {!isMine && (
        <img
          className="sender_img"
          src={otherAvatar}
          alt="sender_avatar"
        />
      )}
      <div className="cm_chat_content">
        {!hasMedia && (mainText || (!tipLine && message) || isTipOnlyMessage) && (
          <div className="cm_chat_text">
            {mainText ? <p>{renderTextWithLinks(mainText)}</p> : null}
            {!mainText && !tipLine && !isTipOnlyMessage ? (
              <p>{renderTextWithLinks(message)}</p>
            ) : null}
            {isTipOnlyMessage && tipLine ? (
              <p>
                <span
                  className={`cm_chat_tip_line${isTipRefunded ? ' cm_chat_tip_line_refunded' : ''}${isTipPendingCollection ? ' cm_chat_tip_line_pending' : ''}`}
                >
                  {renderTextWithLinks(tipLine)}
                </span>
              </p>
            ) : null}
            <ChatMessageStatus pending={pending} failed={failed} />
          </div>
        )}
        {hasMedia && (
          <ChatMediaMessage
            parsedMediaJson={parsedMediaJson}
            isUser={isUser}
            otherAvatar={otherAvatar}
            creatorInfo={creatorInfo}
            messageEventId={item?.eventId ?? null}
            messagePurchaseContentIds={messagePurchaseContentIds}
            onEditMessage={onEditMessage}
          />
        )}
        <div className="cm_chat_meta">
          {!isTipOnlyMessage && tipLine ? (
            <span
              className={`cm_chat_tip_line${isTipRefunded ? ' cm_chat_tip_line_refunded' : ''}${isTipPendingCollection ? ' cm_chat_tip_line_pending' : ''}`}
              aria-label="Tip amount"
            >
              {tipLine}
            </span>
          ) : null}
          <time>{timeLabel}</time>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ChatCard);
